// **Redux Imports
import { createSlice } from '@reduxjs/toolkit'
import { buildFolderAndFilesStructure, getTreeNode } from '../../../utils/utils'

const initialValue = {
  editorOptions: {},
  files: {},
  selected: null,
  notebookFiles: [],
  selectedFile: null,
  attemptEndTime: null,
  contestCounterObj: {},
  editorCompilation: {},
  editorDiagnostics: {},
  editorCompletions: [],
  sqlFiles: {
    content: '',
    fullContent: '',
    evaluateResult: {}
  },
  standardOutputLoading: false
}

export const editorSlice = createSlice({
  name: 'editor',
  initialState: {
    ...initialValue
  },
  reducers: {
    setStandardOutputLoading(state, action) {
      state.standardOutputLoading = action.payload
    },
    resetSQLFiles(state, action) {
      state.sqlFiles = {
        content: '',
        fullContent: '',
        evaluateResult: {}
      }
    },
    updateSQLContent(state, action) {
      state.sqlFiles.content = action.payload
    },
    updateSQLFullContent(state, action) {
      state.sqlFiles.fullContent = action.payload
    },
    setEvaluateResult(state, action) {
      state.sqlFiles.evaluateResult = action.payload || {}
    },

    setEditorNotebookFiles(state, action) {
      state.notebookFiles = action.payload
    },
    updateEditorDependency(state, action) {
      state.editorOptions.dependencies = action.payload.map((item) => ({
        ...item
      }))
    },
    updateContestEndTime(state, action) {
      if (
        action?.payload?.attemptStartTime &&
        action?.payload?.attemptEndTime &&
        action?.payload?.currentTime
      ) {
        state.contestCounterObj = {
          currentTime: action?.payload?.currentTime,
          attemptEndTime: action?.payload?.attemptEndTime,
          attemptStartTime: action?.payload?.attemptStartTime
        }
      }
    },
    setEditorOptions(state, action) {
      state.editorOptions = action.payload

      if (
        action?.payload?.attemptStartTime &&
        action?.payload?.attemptEndTime &&
        action?.payload?.currentTime
      ) {
        state.contestCounterObj = {
          currentTime: action?.payload?.currentTime,
          attemptEndTime: action?.payload?.attemptEndTime,
          attemptStartTime: action?.payload?.attemptStartTime
        }
      }
    },
    setEditorCompletions(state, action) {
      state.editorCompletions = action.payload
    },
    setEditorCompilation(state, action) {
      state.editorCompilation = action.payload
    },
    setEditorDiagnostics(state, action) {
      state.editorDiagnostics = action.payload
    },
    setEditorFiles(state, action) {
      const {
        files,
        updatedFiles,
        clearSelectedFile = true,
        updateSelectedFile = false
      } = action.payload

      let fileTree

      if (updatedFiles) {
        fileTree = updatedFiles
        state.files = updatedFiles
      } else {
        const formatedFiles = buildFolderAndFilesStructure(files)
        fileTree = formatedFiles
        state.files = formatedFiles
      }

      if (updateSelectedFile) {
        const treeNode = getTreeNode(fileTree, state?.selectedFile?.path)
        if (treeNode) {
          state.selectedFile = treeNode
        } else {
          state.selected = null
          state.selectedFile = null
        }
      }

      if (clearSelectedFile) {
        state.selected = null
        state.selectedFile = null
        state.editorCompilation = {}
        state.editorDiagnostics = {}
      }
    },

    updateFileContent(state, action) {
      const { label, path, key, value } = action.payload

      const splitPath = path?.split('/')?.slice(0, -1) || []

      let currentLevel = state.files
      for (let i = 0; i < splitPath.length; i++) {
        if (currentLevel?.[splitPath[i]]?.children) {
          currentLevel = currentLevel[splitPath[i]].children
        } else {
          break
        }
      }

      if (currentLevel?.[label]) {
        currentLevel[label][key] = value
      }
    },
    deleteFileOrFolder(state, action) {
      const { parentPath, path, value } = action.payload
      const splitPath = parentPath ? parentPath?.split('/') : []

      let currentLevel = state.files
      for (let i = 0; i < splitPath.length; i++) {
        currentLevel = currentLevel[splitPath[i]].children
      }
      delete currentLevel[value]

      if (state.selectedFile?.path === path) {
        state.selectedFile = null
      }
    },
    updateEditorFolderName(state, action) {
      const { parentPath, updatedTree } = action.payload

      if (!parentPath) {
        state.files = updatedTree
      } else {
        const splitPath = parentPath?.split('/')
        let currentLevel = state.files
        splitPath.forEach((path, index) => {
          if (index === splitPath.length - 1) {
            currentLevel = currentLevel[path]
          } else {
            currentLevel = currentLevel[path].children
          }
        })

        currentLevel.children = updatedTree
      }
    },

    createEditorFileAndFolder(state, { payload }) {
      const { isFolder, selected } = payload
      const selectedPath = selected || state.selected
      if (selectedPath) {
        const splitPath = selectedPath?.split('/')
        let currentLevel = state.files
        for (let i = 0; i < splitPath.length; i++) {
          const key = splitPath[i]

          if (i === splitPath?.length - 1) {
            if (i === 0 && currentLevel?.[key]?.isFolder) {
              currentLevel = currentLevel[key]
            } else if (i !== 0 && currentLevel.children[key]?.isFolder) {
              currentLevel = currentLevel.children[key]
            } else {
              continue
            }
          } else if (i === 0) {
            currentLevel = currentLevel[key]
          } else {
            currentLevel = currentLevel.children[key]
          }
        }

        if (currentLevel.path) {
          currentLevel.children[''] = {
            path: '',
            label: '',
            children: {},
            isFolder,
            parentPath: currentLevel.path,
            ...(!isFolder
              ? { content: '', candidate: { allowView: true, allowEdit: true } }
              : {})
          }
        } else {
          state.files[''] = {
            path: '',
            label: '',
            parentPath: '',
            isFolder,
            children: {},
            ...(!isFolder
              ? { content: '', candidate: { allowView: true, allowEdit: true } }
              : {})
          }
        }
      } else {
        state.files[''] = {
          path: '',
          label: '',
          parentPath: '',
          isFolder,
          children: {},
          ...(!isFolder
            ? { content: '', candidate: { allowView: true, allowEdit: true } }
            : {})
        }
      }
    },
    setSelectedEditor(state, action) {
      state.selected = action.payload
    },
    setSelectedEditorFile(state, action) {
      state.selectedFile = action.payload
    },
    setEvaluateSelectedFile(state, action) {
      const selectedFile = action.payload.selectedFile

      const file = selectedFile
      const path = file?.path || ''
      const label = path?.split('/')?.slice(-1)?.[0] || ''
      const parentPath = path?.split('/')?.slice(0, -1)?.join('/') || ''
      const isFolder = false
      const content = file?.content || ''
      const candidate = file?.candidate || {}

      state.selected = path
      state.selectedFile = {
        path,
        label,
        parentPath,
        isFolder,
        children: {},
        content,
        candidate
      }
    },

    resetEditorStore(state, action) {
      Object.keys(initialValue).forEach((key) => {
        state[key] = initialValue[key]
      })
    }
  }
})

export const {
  resetSQLFiles,
  resetEditorStore,
  updateSQLFullContent,
  setEvaluateResult,
  updateSQLContent,
  setEditorNotebookFiles,
  updateEditorDependency,
  updateContestEndTime,
  setEditorFiles,
  setEditorCompilation,
  updateFileContent,
  setSelectedEditor,
  setEditorDiagnostics,
  setEditorOptions,
  createEditorFileAndFolder,
  updateEditorFolderName,
  deleteFileOrFolder,
  setEvaluateSelectedFile,
  setSelectedEditorFile,
  setEditorCompletions,
  setStandardOutputLoading
} = editorSlice.actions

export default editorSlice.reducer
